import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type TaskSelectionContextValue = {
    selectedIds: number[];
    draggingId: number | null;
    isSelected: (taskId: number) => boolean;
    toggleSelected: (taskId: number) => void;
    selectMany: (taskIds: number[]) => void;
    deselectMany: (taskIds: number[]) => void;
    clearSelected: () => void;
    setDraggingId: (taskId: number | null) => void;
};

const TaskSelectionContext = createContext<TaskSelectionContextValue>({
    selectedIds: [],
    draggingId: null,
    isSelected: () => false,
    toggleSelected: () => undefined,
    selectMany: () => undefined,
    deselectMany: () => undefined,
    clearSelected: () => undefined,
    setDraggingId: () => undefined,
});

export const useTaskSelection = () => useContext(TaskSelectionContext);

export const TaskSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [draggingId, setDraggingId] = useState<number | null>(null);

    const isSelected = useCallback(
        (taskId: number) => selectedIds.includes(Number(taskId)),
        [selectedIds],
    );

    const toggleSelected = useCallback((taskId: number) => {
        const id = Number(taskId);
        setSelectedIds((prev) => (
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        ));
    }, []);

    const selectMany = useCallback((taskIds: number[]) => {
        const unique = Array.from(new Set(taskIds.map(Number).filter((id) => Number.isFinite(id))));
        setSelectedIds(unique);
    }, []);

    const deselectMany = useCallback((taskIds: number[]) => {
        const remove = new Set(taskIds.map(Number));
        setSelectedIds((prev) => prev.filter((id) => !remove.has(id)));
    }, []);

    const clearSelected = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const value = useMemo(() => ({
        selectedIds,
        draggingId,
        isSelected,
        toggleSelected,
        selectMany,
        deselectMany,
        clearSelected,
        setDraggingId,
    }), [selectedIds, draggingId, isSelected, toggleSelected, selectMany, deselectMany, clearSelected]);

    return (
        <TaskSelectionContext.Provider value={value}>
            {children}
        </TaskSelectionContext.Provider>
    );
};
